/**
 * GastoSapRepo sobre Supabase. Misma lógica que DexieGastoSapRepo.
 */

import type { GastoSap } from "@app-types/entities";
import type { GastoSapRepo } from "@core/data/repos/GastoSapRepo";
import { SupabaseBaseRepo } from "./SupabaseBaseRepo";

export class SupabaseGastoSapRepo
  extends SupabaseBaseRepo<GastoSap>
  implements GastoSapRepo
{
  constructor(tenantId: string) {
    super("gastoSap", tenantId);
  }

  async list(filter?: Partial<GastoSap>): Promise<GastoSap[]> {
    const todos = await this.listAll();
    const filtrado = filter
      ? todos.filter((g) =>
          (Object.keys(filter) as (keyof GastoSap)[]).every(
            (k) => filter[k] === undefined || g[k] === filter[k],
          ),
        )
      : todos;
    return filtrado.sort((a, b) =>
      (b.fechaContabilizacion ?? "").localeCompare(a.fechaContabilizacion ?? ""),
    );
  }

  async get(id: string): Promise<GastoSap | null> {
    return this.getById(id);
  }

  async create(
    data: Omit<GastoSap, "id" | "createdAt" | "updatedAt" | "version">,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const entry: GastoSap = {
      ...data,
      id,
      tenantId: this.tenantId,
      createdAt: now,
      updatedAt: now,
      version: 1,
    };
    await this.createRaw(entry);
    return id;
  }

  async update(
    id: string,
    data: Partial<GastoSap>,
    expectedVersion: number,
  ): Promise<void> {
    const actual = await this.get(id);
    if (!actual) throw new Error("Gasto SAP no encontrado");
    if (actual.version !== expectedVersion) {
      throw new Error(
        `Conflicto de versión: el gasto fue modificado por otro usuario (v${actual.version} vs v${expectedVersion}).`,
      );
    }
    const updated: GastoSap = {
      ...actual,
      ...data,
      id: actual.id,
      tenantId: actual.tenantId,
      createdAt: actual.createdAt,
      updatedAt: new Date().toISOString(),
      version: actual.version + 1,
    };
    await this.putRaw(updated);
  }

  async delete(id: string): Promise<void> {
    await this.deleteById(id);
  }

  async insertMany(rows: GastoSap[]): Promise<number> {
    if (rows.length === 0) return 0;
    await this.insertManyRaw(rows);
    return rows.length;
  }
}
